import { phone as phoneLib, nationalId as nationalIdLib } from '@parsina/framework';

/**
 * What the clinic's COUNTRY sets where nothing else has (PM-0218, country
 * configuration §5 «locale»): the rule a NEW national id is judged by, the
 * country an EMPTY phone field starts on, where a map with no pin opens, and
 * whether an address is picked from Iran's provinces or typed.
 *
 * The same rules as the kit's `ui/lib/countryDefaults.js` and the doctor panel's
 * copy — this one exists until the panel's framework pin carries the kit's; then
 * it goes.
 *
 * Each answer is a DEFAULT and nothing more. What the desk picked (the national
 * id's country chip, the phone's country picker) and what the record already
 * holds (a stored E.164 number, a saved pin) win, exactly as they do today.
 *
 * `caps` is `myClinic.capabilities` (hooks/useClinicCapabilities.js). Every
 * answer here is `undefined` — "do what you did before" to every caller — when
 * the capabilities are not known (`null`), when the country leaves a field
 * empty, and for an `IR` country:
 *
 *   * Iran's profile states exactly what this client already does, and
 *   * a clinic that never set its country reads as IR — an Iraqi clinic on IQD
 *     among them, whose forms today already start on +964 and the generic id
 *     (hooks/useClinicRegion.js). It keeps that until its country is set
 *     (contract rule 2); on a deployment outside the Iranian market it is no
 *     Iranian clinic at all.
 *
 * So an Iranian clinic, and every clinic whose country nobody could read, sees
 * no change; a clinic in another country gets that country's defaults.
 */

/** The national-id country for "any rule but Iran's" — the kit's generic rule. */
const GENERIC_COUNTRY = 'ZZ';

const ISO2 = /^[A-Z]{2}$/;

/** `caps`, or null when this client takes no default from them (see above). */
export function usableCapabilities(caps) {
  if (!caps || typeof caps !== 'object') return null;
  if (typeof caps.country !== 'string' || !ISO2.test(caps.country) || caps.country === 'IR') return null;
  return caps;
}

/**
 * The country a NEW national id is judged by, as `NationalIdInput`'s
 * `defaultCountry` and the `nidProblem` helpers take it: `'IR'` where the
 * country's rule is Iran's کد ملی, the clinic's own country — or the generic
 * placeholder — for the generic rule, which never claims a number valid.
 *
 * A rule key this client does not know is the generic rule: a number is never
 * held to a checksum the profile did not name.
 */
export function nationalIdCountryDefault(caps) {
  const c = usableCapabilities(caps);
  if (!c?.nationalIdRule) return undefined;
  if (c.nationalIdRule === 'IR') return 'IR';
  // The clinic's own code when the kit keeps no rule of its own for it — the
  // field's chip then shows that country — else the generic placeholder.
  return nationalIdLib.NATIONAL_ID_RULES[c.country] ? GENERIC_COUNTRY : c.country;
}

/** The country an EMPTY phone field starts on — the clinic's, when the phone catalogue carries it. */
export function phoneCountryDefault(caps) {
  const country = usableCapabilities(caps)?.country;
  return country && phoneLib.findByIso2(country) ? country : undefined;
}

/** `[lat, lng, zoom]` a map with no pin opens on. */
export function mapOpenAtDefault(caps) {
  const view = usableCapabilities(caps)?.mapView;
  if (!view || typeof view !== 'object') return undefined;
  const lat = Number(view.lat);
  const lon = Number(view.lon);
  const zoom = Number(view.zoom);
  if (![lat, lon, zoom].every(Number.isFinite)) return undefined;
  return [lat, lon, Math.trunc(zoom)];
}

/**
 * Whether a clinic address is picked from Iran's provinces and cities:
 * `undefined` = as today (the lists on the Iranian market, typed outside it).
 * The country can only take the lists away — `free_text`, or any model but
 * Iran's, is typed; it never puts Iran's lists on a deployment that has none.
 */
export function iranianProvincesDefault(caps) {
  const model = usableCapabilities(caps)?.addressModel;
  if (!model) return undefined;
  return model === 'iran_provinces' ? undefined : false;
}

/** Every default above, for one set of capabilities. */
export function countryDefaults(caps) {
  return {
    nationalIdCountry: nationalIdCountryDefault(caps),
    phoneCountry: phoneCountryDefault(caps),
    mapOpenAt: mapOpenAtDefault(caps),
    iranianProvinces: iranianProvincesDefault(caps),
  };
}
